import { useCourseTerms } from "api/hooks";
import React from "react";

import { useInavalidate, useToaster } from "utils";
import { courseTermsApi } from "api/courseTermsApi";
import { QUERIES_KEYS } from "queries";
import { CourseTermsFormValues } from "components/templates/AddTermsModal/CreateTermsForm";
import { UpdateTermsFormValues } from "components/templates/AddTermsModal/UpdateTermsForm";
import { PaziaTermsList } from "components/organisms";
import { PaziaInlineLoader } from "components/atoms";
import { PaziaBox } from "components/atoms";
import { useInheritedCategoryTerms } from "api/categories-terms";
import { Link } from "react-router-dom";
import { routes } from "consts";
import { MdOpenInNew } from "react-icons/md";

interface TermsListProps {
  courseId: number;
  categoryId: number;
}
export const TermsList = ({ courseId, categoryId }: TermsListProps): JSX.Element | null => {
  const invaldiateTerms = useInavalidate(QUERIES_KEYS.COURSE_TERMS(courseId));
  const { showSuccess, showError } = useToaster();
  const { terms, isLoading: loadingTerms } = useCourseTerms(courseId);
  // Also the ancestors' terms: the student gets those too (the list used to show only the direct category's)
  const { groups: categoryTermGroups, isLoading: loadingCategoryTerms } = useInheritedCategoryTerms(categoryId);

  const onTermDelete = async (termId: number) => {
    try {
      await courseTermsApi.deleteResource(courseId, termId);
      showSuccess("Regulamin został usunięty.");
    } catch (e) {
      showError("Nie udało się usunąć regulaminu.");
    } finally {
      invaldiateTerms();
    }
  };

  const onCreateSubmit = async (values: CourseTermsFormValues) => {
    try {
      await courseTermsApi.create(courseId, values);
      showSuccess("Regulamin został dodany");
      invaldiateTerms();
    } catch (e) {
      console.error(e);
      showError("Wystąpił błąd podczas dodawania regulaminu");
    }
  };
  const onUpdateSubmit = async (values: UpdateTermsFormValues) => {
    try {
      await courseTermsApi.update(courseId, values.id, values);
      showSuccess("Regulamin został zaktualizowany");
      invaldiateTerms();
    } catch (e) {
      console.error(e);
      showError("Wystąpił błąd podczas aktualizowania regulaminu");
    }
  };

  if (loadingTerms || loadingCategoryTerms)
    return (
      <>
        <div className="text-primary-700 mt-8 pb-1 text-center">Regulaminy</div>
        <div className="flex w-full justify-center">
          <PaziaInlineLoader />
        </div>
      </>
    );
  return (
    <PaziaBox header="Regulaminy">
      {categoryTermGroups.map(({ category, terms: groupTerms }) => (
        <div key={category.id} data-testid="inherited-category-terms" className="mb-4 text-sm text-slate-500 dark:text-slate-400">
          Regulaminy z kategorii{" "}
          <Link
            to={routes.COURSES_CATEGORIES_EDIT_TERMS(String(category.id))}
            className="font-medium text-slate-700 hover:underline dark:text-slate-200"
          >
            „{category.name}”
          </Link>{" "}
          (kursant akceptuje je przy zapisie):
          {groupTerms.map((term) => (
            <div key={term.id} className="text-primary-700 dark:text-primary-300 flex items-center gap-2">
              {term.name}
              <a target="_blank" rel="noreferrer" href={term.link}>
                <MdOpenInNew className="cursor-pointer hover:text-slate-600" size={16} />
              </a>
            </div>
          ))}
        </div>
      ))}
      <PaziaTermsList
        terms={terms}
        onCreateSubmit={onCreateSubmit}
        onUpdateSubmit={onUpdateSubmit}
        onTermDelete={onTermDelete}
      />
    </PaziaBox>
  );
};
